import React, { useEffect, useState } from 'react';
import s from './ChooseLocation.module.scss';
import { ReactComponent as Checked } from 'images/new-icons/radiobutton-checked.svg';
import { ReactComponent as Unchecked } from 'images/new-icons/unchecked_primary_radio_button.svg';
import { ReactComponent as PenIcon } from 'images/new-icons/pen.svg';
import { ReactComponent as PinIcon } from 'images/new-icons/location-arrow.svg';
import Button from 'components/shared/Button';
import { useTranslation } from 'react-i18next';
import { Map, AdvancedMarker, MapCameraChangedEvent } from '@vis.gl/react-google-maps';
import useIsAuth from 'sdk/hooks/useIsAuth';
import ProfileMenuHeader from 'modules/app/components/ProfileMenu/ProfileMenuHeader';

const ChooseLocation = ({
                            handleNext,
                            setHomeLocation,
                            homeLocation,
                            setSelectedLocation,
                            allowedLocations = 'both',
                            preSelectedLocationType = null
                        }: any) => {
    const { t } = useTranslation() as any;
    const { data: isAuthData } = useIsAuth();

    const getInitialLocationType = () => {
        if (preSelectedLocationType) {
            if (preSelectedLocationType === 'At Home') {
                return 'home';
            }
            if (preSelectedLocationType === 'In Salon') {
                return 'salon';
            }
        }
        return 'salon';
    };

    const [locationType, setLocationType] = useState(getInitialLocationType());
    const [showMap, setShowMap] = useState(false);
    const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral | null>(null);
    const [userLocation, setUserLocation] = useState<google.maps.LatLngLiteral | null>(null);
    const [savedAddress, setSavedAddress] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [zoom, setZoom] = useState(15);
    const [displayedAddress, setDisplayedAddress] = useState('');
    const [addressLoading, setAddressLoading] = useState(false);

    // Debounce hook
    const useDebounce = (value: any, delay: number) => {
        const [debouncedValue, setDebouncedValue] = useState(value);

        useEffect(() => {
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);

            return () => clearTimeout(handler);
        }, [value, delay]);

        return debouncedValue;
    };

    const debouncedMapCenter = useDebounce(mapCenter, 500);

    // Current location
    useEffect(() => {
        const getLocation = async () => {
            try {
                const response = await fetch(
                    `https://www.googleapis.com/geolocation/v1/geolocate?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`,
                    { method: 'POST' }
                );
                const data = await response.json();

                if (data.location) {
                    const location = {
                        lat: data.location.lat,
                        lng: data.location.lng,
                    };
                    setUserLocation(location);
                    setMapCenter(location);
                    return;
                }
            } catch (error) {
                console.error('Google Geolocation error:', error);
            }

            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    position => {
                        const location = {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                        };
                        setUserLocation(location);
                        setMapCenter(location);
                    },
                    error => {
                        console.error('Geolocation error:', error);
                        const fallback = { lat: 24.7136, lng: 46.6753 };
                        setUserLocation(fallback);
                        setMapCenter(fallback);
                    }
                );
            }
        };

        getLocation();
    }, []);

    useEffect(() => {
        const user = isAuthData?.user;
        if (!user) return;

        setHomeLocation(prev => {
            const name =
                prev.name ||
                user.user_metadata?.full_name ||
                user.email?.split('@')[0] ||
                '';

            const phone =
                prev.phone ||
                user.phone ||
                '';

            if (prev.name === name && prev.phone === phone) return prev;

            return { ...prev, name, phone };
        });
    }, [isAuthData]);

    // Fetch address when map center changes
    useEffect(() => {
        if (!debouncedMapCenter || !showMap) return;

        const fetchAddress = async () => {
            setAddressLoading(true);
            try {
                const response = await fetch(
                    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${debouncedMapCenter.lat},${debouncedMapCenter.lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
                );

                const data = await response.json();

                if (data.status === 'OK' && data.results?.length) {
                    // Filter out plus codes results to find a more human-readable one if possible
                    const result = data.results.find((r: any) => !r.types.includes('plus_code')) || data.results[0];
                    const components = result.address_components;

                    let street = '';
                    let houseNumber = '';

                    components.forEach(comp => {
                        if (comp.types.includes('route')) street = comp.long_name;
                        if (comp.types.includes('street_number')) houseNumber = comp.long_name;
                    });

                    let fullAddress = houseNumber
                        ? `${street}, ${houseNumber}`
                        : (street || result.formatted_address);

                    // Remove Plus Code from the start of the address (e.g., "34PW+PH7 Marsa Dubai")
                    if (fullAddress && fullAddress.includes('+')) {
                        const parts = fullAddress.split(' ');
                        // Plus codes are usually the first part and contain a '+'
                        if (parts[0].includes('+') && parts[0].length <= 11) {
                            fullAddress = parts.slice(1).join(' ').replace(/^[, -]+/, '');
                        }
                    }

                    setDisplayedAddress(fullAddress);
                }
            } catch (error) {
                console.error('Geocoding error:', error);
            } finally {
                setAddressLoading(false);
            }
        };

        fetchAddress();
    }, [debouncedMapCenter, showMap]);

    const handleMapCameraChange = (ev: MapCameraChangedEvent) => {
        if (ev.detail.center) {
            setMapCenter(ev.detail.center);
        }
        if (ev.detail.zoom !== undefined) {
            setZoom(ev.detail.zoom);
        }
    };

    const handleConfirmLocation = async () => {
        if (!displayedAddress) {
            alert(t('Please wait for address to load'));
            return;
        }

        setHomeLocation({
            street: displayedAddress,
            apartment: '',
            floor: '',
            entrance: '',
            note: '',
            intercom: '',
            name: homeLocation.name,
            phone: homeLocation.phone,
        });

        setSavedAddress(displayedAddress);
        setSelectedLocation('At Home');
        setShowMap(false);
    };

    useEffect(() => {
        if (locationType === 'home') {
            setSelectedLocation('At Home');
        } else if (locationType === 'salon') {
            setSelectedLocation('In Salon');
        }
    }, [locationType]);

    const isLocationLocked = !!preSelectedLocationType;

    if (showMap) {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999,
                backgroundColor: 'white'
            }}>
                <div style={{
                    width: '100%',
                    padding: '12px 12px'
                }}>
                    <ProfileMenuHeader
                        text="Select location"
                        onClose={() => setShowMap(false)}
                    />
                </div>

                <div style={{
                    width: '100%',
                    height: '100%',
                    position: 'relative'
                }}>
                    <Map
                        mapId={'661e50b2cb212eb1'}
                        zoom={zoom}
                        center={mapCenter || { lat: 24.7136, lng: 46.6753 }}
                        onCameraChanged={handleMapCameraChange}
                        gestureHandling={'greedy'}
                        zoomControl={true}
                        mapTypeControl={false}
                        streetViewControl={false}
                        fullscreenControl={false}
                        rotateControl={false}
                        disableDefaultUI={false}
                        styles={[
                            {
                                "featureType": "all",
                                "elementType": "geometry",
                                "stylers": [{ "color": "#f5f5f5" }]
                            },
                            {
                                "featureType": "road",
                                "elementType": "geometry",
                                "stylers": [{ "color": "#ffffff" }]
                            },
                            {
                                "featureType": "road",
                                "elementType": "geometry.stroke",
                                "stylers": [{ "color": "#e0e0e0" }]
                            },
                            {
                                "featureType": "road",
                                "elementType": "labels.text.fill",
                                "stylers": [{ "color": "#666666" }]
                            },
                            {
                                "featureType": "road",
                                "elementType": "labels.text.stroke",
                                "stylers": [{ "color": "#ffffff" }, { "weight": 2 }]
                            },
                            {
                                "featureType": "poi",
                                "elementType": "geometry",
                                "stylers": [{ "color": "#eeeeee" }]
                            },
                            {
                                "featureType": "poi",
                                "elementType": "labels.text.fill",
                                "stylers": [{ "color": "#757575" }]
                            },
                            {
                                "featureType": "poi",
                                "elementType": "labels.text.stroke",
                                "stylers": [{ "color": "#ffffff" }]
                            },
                            {
                                "featureType": "poi",
                                "elementType": "labels.icon",
                                "stylers": [{ "visibility": "on" }]
                            },
                            {
                                "featureType": "poi.park",
                                "elementType": "geometry",
                                "stylers": [{ "color": "#e8e8e8" }]
                            },
                            {
                                "featureType": "water",
                                "elementType": "geometry",
                                "stylers": [{ "color": "#d0d0d0" }]
                            },
                            {
                                "featureType": "water",
                                "elementType": "labels.text.fill",
                                "stylers": [{ "color": "#666666" }]
                            },
                            {
                                "featureType": "administrative",
                                "elementType": "labels.text.fill",
                                "stylers": [{ "color": "#444444" }]
                            },
                            {
                                "featureType": "landscape",
                                "elementType": "geometry",
                                "stylers": [{ "color": "#f0f0f0" }]
                            }
                        ]}
                    >
                        {userLocation && (
                            <AdvancedMarker position={userLocation}>
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: '#4285F4',
                                    border: '3px solid white',
                                    borderRadius: '50%',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                                }}>
                                </div>
                            </AdvancedMarker>
                        )}
                    </Map>

                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -100%)',
                        zIndex: 1000,
                        pointerEvents: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}>
                        <style>
                            {`
                                @keyframes bounce {
                                    0%, 100% { transform: translateY(0); }
                                    50% { transform: translateY(-5px); }
                                }
                            `}
                        </style>
                        <svg width="40" height="56" viewBox="0 0 40 56" style={{
                            filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.2))',
                            animation: 'bounce 1s ease-in-out infinite'
                        }}>
                            <circle cx="20" cy="18" r="12" fill="#367F9A" stroke="#367F9A" strokeWidth="2.5"/>
                            <circle cx="20" cy="18" r="5" fill="white"/>
                            <path d="M 20 30 Q 20 40, 20 50" stroke="#367F9A" strokeWidth="3" fill="none" strokeLinecap="round"/>
                        </svg>
                        <div style={{
                            width: '24px',
                            height: '8px',
                            background: 'radial-gradient(ellipse, rgba(0,0,0,0.25), transparent)',
                            borderRadius: '50%',
                            marginTop: '-8px'
                        }}></div>
                    </div>

                    <div className={s.address_display}>
                        <div className={s.address_text}>
                            {addressLoading ? t('Loading address') : displayedAddress || t('Move the map to select location')}
                        </div>
                    </div>

                    <button
                        onClick={() => userLocation && setMapCenter(userLocation)}
                        style={{
                            position: 'absolute',
                            bottom: '24%',
                            right: '6%',
                            width: '40px',
                            height: '40px',
                            backgroundColor: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '24px',
                            zIndex: 100
                        }}
                    >
                        <PinIcon className={s.location_mark}></PinIcon>
                    </button>
                </div>

                <div style={{
                    position: 'absolute',
                    bottom: '24px',
                    left: 0,
                    right: 0,
                    padding: '15px 20px',
                    zIndex: 10000
                }}>
                    <Button
                        wide
                        darkGreen
                        onClick={handleConfirmLocation}
                        disabled={isLoading || !displayedAddress}
                        style={{
                            width: '100%',
                            padding: '16px',
                            fontSize: '16px',
                            fontWeight: '600'
                        }}
                    >
                        {isLoading ? t('Loading...') : t("I'm here")}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className={s.choose_location}>
            <div className={s.choose_location_title}>{t('Choose location')}</div>
            <div className={s.choose_location_cards}>
                <div
                    className={s.choose_location_card}
                    onClick={() => {
                        if (!isLocationLocked) {
                            setLocationType('salon');
                            setSelectedLocation('In Salon');
                            setHomeLocation({
                                street: '',
                                apartment: '',
                                floor: '',
                                entrance: '',
                                note: '',
                                intercom: '',
                                name: homeLocation.name,
                                phone: homeLocation.phone,
                            });
                            setSavedAddress('');
                        }
                    }}
                    style={{
                        opacity: isLocationLocked && locationType !== 'salon' ? 0.5 : 1,
                        cursor: isLocationLocked && locationType !== 'salon' ? 'not-allowed' : 'pointer'
                    }}
                >
                    <div className={s.choose_location_card_content}>
                        <div className={s.choose_location_card_title}>{t('In Salon')}</div>
                        <div className={s.choose_location_card_description}>
                            {t('The master will receive you in the salon')}
                        </div>
                    </div>
                    <div className={s.choose_location_card_checkbox}>
                        {locationType === 'salon' ? <Checked /> : <Unchecked />}
                    </div>
                </div>
                <div
                    className={s.choose_location_card}
                    onClick={() => {
                        if (!isLocationLocked) {
                            setLocationType('home');
                            setSelectedLocation('At Home');
                        }
                    }}
                    style={{
                        opacity: isLocationLocked && locationType !== 'home' ? 0.5 : 1,
                        cursor: isLocationLocked && locationType !== 'home' ? 'not-allowed' : 'pointer'
                    }}
                >
                    <div className={s.choose_location_card_content}>
                        <div className={s.choose_location_card_title}>{t('At Home')}</div>
                        <div className={s.choose_location_card_description}>
                            {t('The master will come to your home')}
                        </div>
                    </div>
                    <div className={s.choose_location_card_checkbox}>
                        {locationType === 'home' ? <Checked /> : <Unchecked />}
                    </div>
                </div>
            </div>

            {locationType === 'home' && (
                <>
                    {savedAddress ? (
                        <div className={s.saved_address}>
                            <span>{savedAddress}</span>
                            <PenIcon onClick={() => {
                                setSavedAddress('');
                                setHomeLocation({
                                    street: '',
                                    apartment: '',
                                    floor: '',
                                    entrance: '',
                                    note: '',
                                    intercom: '',
                                    name: homeLocation.name,
                                    phone: homeLocation.phone,
                                });
                                setShowMap(true);
                            }} />
                        </div>
                    ) : (
                        <Button wide yellowDarkGreen onClick={() => setShowMap(true)}>
                            {t('Add your address')}
                        </Button>
                    )}
                </>
            )}

            <Button
                wide
                darkGreen
                onClick={() => handleNext && handleNext()}
                disabled={locationType === 'home' && !savedAddress}
                className={s.book_button}
            >
                {t('Book now')}
            </Button>
        </div>
    );
};

export default ChooseLocation;